import { classes } from "polytype";
import {
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base/index.js";
import type {
  MySqlUserAccessLevel,
  MySqlUserCreateRequestData,
  MySqlUserData,
  MySqlUserListItemData,
  MySqlUserListQueryData,
  MySqlUserUpdateRequestData,
} from "./types.js";
import { type AsyncResourceVariant, provideReact } from "../../react/index.js";
import { config } from "../../config/config.js";
import assertObjectFound from "../../base/assertObjectFound.js";
import type { MySql } from "../MySql/index.js";

export class MySqlUser extends ReferenceModel {
  public static ofId(id: string) {
    return new MySqlUser(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.mySqlUser.find(id);
    if (data !== undefined) {
      return new MySqlUserDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const databaseUser = await this.find(id);
    assertObjectFound(databaseUser, "mySqlDatabaseUser", id);
    return databaseUser;
  });

  public findDetailed = provideReact(
    () => MySqlUser.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<MySqlUserDetailed | undefined>>;

  public getDetailed = provideReact(
    () => MySqlUser.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<MySqlUserDetailed>>;

  public static query(database: MySql, query: MySqlUserListQueryData = {}) {
    return new MySqlUserListQuery(database, query);
  }

  public static async create(mySql: MySql, data: MySqlUserCreateRequestData) {
    const response = await config.behaviors.mySqlUser.create(mySql.id, data);

    if ("id" in response) {
      return new MySqlUser(response.id);
    }

    return response;
  }

  public openPhpMyAdmin = provideReact(async () => {
    const phpMyAdmin = await config.behaviors.mySqlUser.getPhpMyAdminUrl(
      this.id,
    );

    window.open(phpMyAdmin, "_blank");
  }, [this.id]);

  public async delete() {
    await config.behaviors.mySqlUser.delete(this.id);
  }

  public async updatePassword(password: string) {
    await config.behaviors.mySqlUser.updatePassword(this.id, password);
  }

  public async update(data: MySqlUserUpdateRequestData) {
    await config.behaviors.mySqlUser.update(this.id, data);
  }
}

class MySqlUserCommon extends classes(
  DataModel<MySqlUserListItemData | MySqlUserData>,
  MySqlUser,
) {
  public readonly name: string;
  public readonly mainUser: boolean;
  public readonly description: string;
  public readonly accessLevel: MySqlUserAccessLevel;
  public readonly externalAccess: boolean;
  public readonly isReady: boolean;
  public constructor(data: MySqlUserListItemData | MySqlUserData) {
    super([data], [data.id]);
    this.name = data.name;
    this.mainUser = data.mainUser;
    this.description = data.description ?? "";
    this.accessLevel = data.accessLevel;
    this.externalAccess = data.externalAccess;
    this.isReady = data.status === "ready";
  }
}

export class MySqlUserDetailed extends classes(
  MySqlUserCommon,
  DataModel<MySqlUserData>,
) {
  public constructor(data: MySqlUserData) {
    super([data], [data]);
  }
}

export class MySqlUserListItem extends classes(
  MySqlUserCommon,
  DataModel<MySqlUserListItemData>,
) {
  public constructor(data: MySqlUserListItemData) {
    super([data], [data]);
  }
}

export class MySqlUserListQuery extends ListQueryModel<MySqlUserListQueryData> {
  public readonly database: MySql;

  public constructor(database: MySql, query: MySqlUserListQueryData = {}) {
    super(query, {
      dependencies: [database.id],
    });
    this.database = database;
  }

  public refine(query: MySqlUserListQueryData) {
    return new MySqlUserListQuery(this.database, {
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items } = await config.behaviors.mySqlUser.list(
      this.database.id,
      this.query,
    );
    return new MySqlUserList(
      this.database,
      this.query,
      items.map((d) => new MySqlUserListItem(d)),
    );
  }, [this.queryId, "mySql"]);
}

export class MySqlUserList extends classes(
  MySqlUserListQuery,
  ListDataModel<MySqlUserListItem>,
) {
  public constructor(
    database: MySql,
    query: MySqlUserListQueryData,
    mySqlUsers: MySqlUserListItem[],
  ) {
    super([database, query], [mySqlUsers, mySqlUsers.length]);
  }
}
